import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { TextColors, BackgroundColors } from '@picsart/design-system/foundation/colors';

import { Breakpoints } from '@/lib/breakpoints';
import { DISMISSED_ATTRIBUTE } from './constants/dismissal';
import { ACCENT, ACCENT_GRADIENT, ON_ACCENT, iconPaint, focusRing } from '@/components/HeaderAuthSection/styleTokens';

const CLOSE_SIZE = 36;
const CLOSE_INSET = 4;

/**
 * Modelled on Kling AI's mobile web strip: one line of copy, a filled pill
 * and a close mark, on a surface a step lighter than the header with a faint
 * accent glow behind the centred copy.
 */
const useStyles = createUseStyles(
  {
    // Hidden by CSS rather than by reading the viewport, so the CDN-cached HTML
    // is the same for every visitor and nothing shifts on hydration.
    //
    // Copy and button are centred as a pair; the close mark is pinned to the
    // right edge out of the flow, and the equal side padding keeps the pair
    // centred on the screen and clear of it.
    banner: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: Spacings.s10,
      boxSizing: 'border-box',
      height: 44,
      padding: `0 ${CLOSE_SIZE + CLOSE_INSET}px`,
      background: `radial-gradient(60% 140% at 50% 50%, ${ACCENT}29, transparent 70%), ${BackgroundColors.tint1}`,
      [Breakpoints.MD]: { display: 'none' },
      [`[${DISMISSED_ATTRIBUTE}] &`]: { display: 'none' },
    },
    text: {
      minWidth: 0,
      fontSize: FontSizes.t3,
      fontWeight: FontWeights.semibold,
      lineHeight: LineHeights.t3,
      color: TextColors.base,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    open: {
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      height: 24,
      padding: `0 ${Spacings.s10}`,
      borderRadius: CornerRadius.r16,
      // The brand gradient the header already uses for the avatar ring and the
      // Projects pill, so the strip's one action reads as part of the same palette.
      background: ACCENT_GRADIENT,
      color: ON_ACCENT,
      fontSize: FontSizes.t3,
      fontWeight: FontWeights.semibold,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      transition: 'transform 120ms ease',
      '&:active': { transform: 'scale(0.96)' },
      ...focusRing,
    },
    // 36px to tap, a 16px mark to see.
    close: {
      position: 'absolute',
      top: '50%',
      right: CLOSE_INSET,
      transform: 'translateY(-50%)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: CLOSE_SIZE,
      height: CLOSE_SIZE,
      padding: 0,
      border: 'none',
      background: 'none',
      // Muted, so the pill stays the one thing that draws the eye.
      color: TextColors.tint1,
      cursor: 'pointer',
      ...focusRing,
    },
    closeIcon: { ...iconPaint, width: 16, height: 16 },
  },
  { name: 'app-banner' },
);

export default useStyles;
